import { z } from 'zod';
import type { NextEnv, ExpoEnv, ValidatedEnv, Environment } from './types';
import { resolve } from 'path';
import dotenv from 'dotenv';

// Common validation schemas
const urlSchema = z.string().url().min(1).or(z.string().min(1));
const keySchema = z.string().min(1);
const portSchema = z.coerce.number().int().positive().optional();
const envSchema = z.enum(['development', 'staging', 'production']).default('development');
const debugSchema = z.coerce.boolean().default(false);

// Helper to safely check environment
const safeGetEnv = () => {
  try {
    return process.env || {};
  } catch {
    return {};
  }
};

const isDevelopment = safeGetEnv().NODE_ENV === 'development' || 
  safeGetEnv().NEXT_PUBLIC_APP_ENV === 'development';
const isServer = typeof window === 'undefined';

// Debug logging that works in both environments
const debugLog = (message: string, data?: any) => {
  if (isDevelopment || safeGetEnv().DEBUG === 'true' || safeGetEnv().NEXT_PUBLIC_DEBUG === 'true') {
    const logPrefix = `[ENV ${isServer ? 'Server' : 'Client'}]`;
    try {
      console.log(logPrefix, message, data ? JSON.stringify(data, null, 2) : '');
    } catch {
      console.log(logPrefix, message);
    }
  }
};

// Platform-aware path resolution with better error handling
const resolvePath = (path: string) => {
  try {
    // Get the current working directory
    const cwd = process.cwd();
    debugLog(`Resolving path from CWD: ${cwd}`, { path });
    
    // Try to resolve the path
    const resolved = resolve(cwd, path);
    debugLog(`Resolved path: ${resolved}`);
    return resolved;
  } catch (error) {
    debugLog(`Error resolving path: ${path}`, { error });
    return path;
  }
};

// Improved development defaults loader with better path detection
const loadDevDefaults = () => {
  debugLog('Loading development defaults');
  
  try {
    // Try multiple possible paths for development env
    // Start with app-specific .env files, then try monorepo root
    const possiblePaths = [
      '.env.development',
      '.env',
      '../.env.development',
      '../.env',
      '../../.env.development',
      '../../.env',
      // If a specific path is set, try that too
      safeGetEnv().DEV_ENV_PATH
    ].filter(Boolean);

    debugLog('Checking possible env paths', { possiblePaths });

    for (const path of possiblePaths) {
      if (!path) continue;
      
      const resolvedPath = resolvePath(path);
      debugLog(`Checking env file: ${resolvedPath}`);
      
      try {
        const result = dotenv.config({ path: resolvedPath });
        
        if (result.error) {
          debugLog(`Error loading env from ${resolvedPath}`, { error: result.error.message });
          continue;
        }
        
        if (result.parsed && Object.keys(result.parsed).length > 0) {
          debugLog(`Loaded env from ${resolvedPath}`, { 
            variables: Object.keys(result.parsed),
            count: Object.keys(result.parsed).length 
          });
          return result.parsed;
        }
      } catch (error) {
        debugLog(`Exception loading env from ${resolvedPath}`, { error });
      }
    }
    
    debugLog('No environment files found');
    return {};
  } catch (error) {
    debugLog('Error in loadDevDefaults', { error });
    return {};
  }
};

// Web-specific environment schema (Next.js)
export const webEnvSchema = z.object({
  // Client-side variables
  NEXT_PUBLIC_SUPABASE_URL: urlSchema,
  NEXT_PUBLIC_SUPABASE_ANON_KEY: keySchema,
  NEXT_PUBLIC_APP_URL: urlSchema,
  NEXT_PUBLIC_APP_ENV: envSchema,
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  NEXT_PUBLIC_DEBUG: debugSchema,
  
  // Server-side variables
  DATABASE_URL: z.string().optional(),
  SUPABASE_SERVICE_ROLE_KEY: z.string().optional(),
  STRIPE_SECRET_KEY: z.string().optional(),
  STRIPE_WEBHOOK_SECRET: z.string().optional(),
  SESSION_SECRET: z.string().min(32).optional(),
  PORT: portSchema,
}) satisfies z.ZodType<NextEnv>;

// Mobile-specific environment schema (Expo)
export const mobileEnvSchema = z.object({
  EXPO_PUBLIC_SUPABASE_URL: urlSchema,
  EXPO_PUBLIC_SUPABASE_ANON_KEY: keySchema,
  EXPO_PUBLIC_APP_URL: urlSchema,
  EXPO_PUBLIC_APP_ENV: envSchema,
  EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  EXPO_PUBLIC_DEBUG: debugSchema,
}) satisfies z.ZodType<ExpoEnv>;

// Platform-aware environment validation
export const validateEnv = (env: Record<string, any>, platform: 'Expo' | 'Next.js'): ValidatedEnv => {
  const isExpo = platform === 'Expo';
  
  debugLog('Validating environment:', {
    platform,
    environment: isServer ? 'server' : 'client',
    isDevelopment
  });

  try {
    // In development, try to load defaults from .env.development
    if (isDevelopment) {
      const devDefaults = loadDevDefaults();
      debugLog('Loaded development defaults:', { 
        hasDefaults: Object.keys(devDefaults).length > 0,
        platform 
      });

      const mergedEnv = { ...devDefaults, ...env };

      if (!isExpo) {
        const result = webEnvSchema.parse(mergedEnv);
        debugLog('Web environment validated successfully');
        return result;
      }

      const result = mobileEnvSchema.parse(mergedEnv);
      debugLog('Mobile environment validated successfully');
      return result;
    }

    // In production, use strict validation
    if (!isExpo) {
      const result = webEnvSchema.parse(env);
      debugLog('Web environment validated successfully');
      return result;
    }

    const result = mobileEnvSchema.parse(env);
    debugLog('Mobile environment validated successfully');
    return result;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map(e => e.path.join('.')).join(', ');
      const errorMessage = `Environment validation failed for ${platform}.\n` +
        `Missing required variables: ${missingVars}\n` +
        'Please check your .env file and ensure all required variables are set.\n' +
        `For development, create a .env.development file in your ${isExpo ? 'mobile' : 'web'} app directory.`;
      
      debugLog('Validation error:', { error: errorMessage });
      
      // In development, try to continue with defaults even with validation errors
      if (isDevelopment) {
        debugLog('Attempting to continue with defaults in development mode');
        
        const devDefaults = loadDevDefaults();
        if (Object.keys(devDefaults).length > 0) {
          debugLog('Using development defaults from .env file');
          
          try {
            // Try again with the development defaults, but use partial validation
            // to allow the build to proceed even if not all variables are present
            if (!isExpo) {
              const relaxedSchema = webEnvSchema.partial();
              const result = relaxedSchema.parse({ ...devDefaults, ...env });
              debugLog('Using relaxed validation for development');
              return result as ValidatedEnv; // Type assertion needed here
            } else {
              const relaxedSchema = mobileEnvSchema.partial();
              const result = relaxedSchema.parse({ ...devDefaults, ...env });
              debugLog('Using relaxed validation for development');
              return result as ValidatedEnv; // Type assertion needed here
            }
          } catch (secondError) {
            debugLog('Failed to validate even with defaults and relaxed schema', { secondError });
          }
        }
      }
      
      throw new Error(errorMessage);
    }
    throw error;
  }
};

// Helper to check if required variables are set
export const checkRequiredEnvVars = (env: ValidatedEnv, platform: 'Expo' | 'Next.js') => {
  const isExpo = platform === 'Expo';
  debugLog('Checking required environment variables');

  // Skip validation in development for Expo
  if (isDevelopment && isExpo) {
    debugLog('Skipping required variables check in Expo development mode');
    return;
  }

  const schema = !isExpo ? webEnvSchema : mobileEnvSchema;
  const missingVars: string[] = [];
  
  Object.entries(schema.shape).forEach(([key, value]) => {
    // @ts-expect-error - We know these keys exist on the env object
    if (!value.isOptional() && !env[key]) {
      missingVars.push(key);
    }
  });

  if (missingVars.length > 0) {
    const errorMessage = `Missing required environment variables for ${platform}: ${missingVars.join(', ')}\n` +
      'Please check your .env file and ensure all required variables are set.\n' +
      `For development, create a .env.development file in your ${isExpo ? 'mobile' : 'web'} app directory.`;
    
    debugLog('Missing required variables:', { missingVars });
    throw new Error(errorMessage);
  }

  debugLog('All required variables are present');
};